import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { streamingDataSlice } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppExtraArgument, AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import {
  type Allocation,
  AllocationValueTypeEnum,
  BaseField,
  type CareOrder,
  type Currency,
  EMPTY_ARRAY,
  EMPTY_SUB_ACCOUNT_SELECTION,
  ExpressedOutcomeEnum,
  Field,
  getExpirationByMarketByCurrencyIdentity,
  getGroup,
  getScaleFromIncrement,
  getStrikesByOptionKey,
  getTypedKeys,
  isOrder,
  ModeEnum,
  NumericField,
  Order,
  OrderMarketStatusEnum,
  PricingModeEnum,
  type ProductGroup,
  type Quote,
  RFQTradingProtocolEnum,
  type Security,
  SecurityTypesUtils,
  SelectorField,
  SideEnum,
  SubProductTypeEnum,
  toBigWithDefault,
  Unit,
} from '@talos/kyoko';
import { freeze, isDraft, original, type WritableDraft } from 'immer';
import { compact } from 'lodash-es';
import type { MarketSelectorItem, MarketSelectorValue } from '../../MarketSelector/types';
import { OrderMarketsField } from '../../MarketSelector/utils/OrderMarketsField';
import { getMarketSelectorItemsListener } from '../Common/marketSelectionSliceUtils';
import { Option, type OptionModelReferenceData } from '../NewOrder/models';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { OMSView } from '../OMSView';
import {
  disableAllocationFields,
  mapAllocationsToField,
  touchAllocations,
  updateSubAccountOrAllocationState,
  validateAllocations,
} from '../allocationUtils';
import { numberIsPositive } from '../commonFieldRules';
import type { OMSReferenceDataState } from '../types';
import { careOrderQuantityValidation, quantityValidation } from './FieldRules';
import { FixingField } from './FixingField';
import {
  getInitialOTCRepoFormState,
  initOTCRepoStateOnProductGroupSet,
  otcRepoFormReducers,
  touchOTCRepoForm,
} from './OTCRepos/otcRepoState';
import {
  createRFQAssetCurrency,
  getRFQCurrencyAsset,
  getRFQFormCurrencySpec,
  isRFQContractsCurrency,
  resolveRFQCurrencyFromEntity,
  rfqCurrencyEquals,
} from './currencies';
import { buildMarketSelectorItems, initializeRFQMarketsFieldThunk } from './rfqMarketInitialization';
import { rfqSelectors } from './selectors';
import {
  getParentTypeFromParentOrder,
  isCareOrWorkingOrderParentType,
  resolveParentTypeFromParentOrder,
  startCareOrderParentListener,
} from './tradeCareOrder';
import type { PrimeRFQParams, RFQCurrency, RFQDependencies, RFQParentType, RFQState } from './types';
import {
  isEphemeralRFQSecurity,
  isRFQSupportedOption,
  isRFQSweepingAvailable,
  RFQSLICE_POSITION_REQUEST_PARAMS,
  securityHasSizeCounteringFeatureFlagEnabled,
  STS_DIGITAL_MARKET_NAME,
} from './utils';

export const getInitialState = (): RFQState => ({
  referenceData: initialRefDataState,
  dependencies: {
    tradableSubAccounts: EMPTY_ARRAY,
  },
  form: {
    fundField: new Field<string>({ name: 'Fund', isRequired: false }),
    symbolField: new Field<Security>({ name: 'Symbol' }),
    fixingField: new FixingField(),
    sideField: new Field({ name: 'Side', isRequired: false }),
    outcomeField: new Field<ExpressedOutcomeEnum | undefined>({ name: 'Outcome', isRequired: false }),
    quantityField: new NumericField({ name: 'Quantity' }),
    rfqCurrencyField: new SelectorField(),
    rfqMarketsField: new OrderMarketsField({ name: 'Market Account' }),
    groupField: new Field({ name: 'Group', isRequired: false }),
    allocationValueTypeField: new Field({ value: AllocationValueTypeEnum.Percentage }),
    allocations: EMPTY_ARRAY,
    parentIDField: new Field({ name: 'Parent ID', isRequired: false }),
    parentType: undefined,
    assignToParentField: new Field({ name: 'Assign to Parent', isRequired: false }),
    pricingModeField: new Field({ name: 'Pricing Mode', isRequired: false }),
    clearingAccountField: new Field<string>({ name: 'Clearing Account', isRequired: false }),
    rfqTradingProtocolField: new Field({
      name: 'RFQ Protocol',
      isRequired: false,
      value: 'Streaming',
    }),
    externalIDField: new Field({ name: 'External ID', isRequired: false, isDisabled: true }), // externalID is always disabled in RFQ
    externalCommentsField: new Field({ name: 'External Comments', isRequired: false, isDisabled: true }),
    settleDateField: new Field({ name: 'Settlement Date', isRequired: false, isDisabled: true }), // settleDate is always disabled in RFQ
    splitRFQs: new Field<boolean>({ name: 'Sweeping', value: false }),
    sizeCountering: new Field<boolean>({ name: 'Size Countering', value: false }),
    option: undefined,
  },
  otcRepoForm: getInitialOTCRepoFormState(),
  productGroup: 'Spot',
  subProductType: undefined,
  isLoading: false,
  initialized: false,
  quoteRequested: false,
  currentQuote: undefined,
  currentCareOrder: undefined,
});

export const RFQ_SPREAD_TO_FIXING_PRODUCT_GROUPS = ['Spot', 'Compound'] as const satisfies readonly ProductGroup[];

export function productGroupSupportsRFQSpreadToFixing(productGroup: ProductGroup | undefined): boolean {
  return RFQ_SPREAD_TO_FIXING_PRODUCT_GROUPS.some(supportedProductGroup => supportedProductGroup === productGroup);
}

function updateProductGroup(state: WritableDraft<RFQState>, productGroup: ProductGroup): void {
  state.productGroup = productGroup;
  state.subProductType = productGroup === 'OTC' ? SubProductTypeEnum.OTCRepo : undefined;
}

function handlePricingModeChange(state: WritableDraft<RFQState>, pricingMode: PricingModeEnum | undefined) {
  state.form.pricingModeField = state.form.pricingModeField.updateValue(pricingMode);
  state.form.fixingField = state.form.fixingField.setIsRequired(
    state.form.pricingModeField.isVisible && pricingMode === PricingModeEnum.SpreadToFixing
  );
}

function applyPredictionOutcomeState(state: WritableDraft<RFQState>, outcome?: ExpressedOutcomeEnum): void {
  state.form.sideField = state.form.sideField.updateValue(undefined);
  state.form.outcomeField = state.form.outcomeField
    .setIsRequired(true)
    .updateValue(outcome ?? ExpressedOutcomeEnum.Yes);
}

const initializeFieldsFromOrder = (
  state: WritableDraft<RFQState>,
  order: Order,
  resolvedParentType?: RFQParentType
) => {
  handleSymbolChange(state, order.Symbol);

  state.form.sideField = state.form.sideField.updateValue(order.Side === SideEnum.Buy ? SideEnum.Buy : SideEnum.Sell);
  if (SecurityTypesUtils.PredictionOutcome.isProductType(state.form.symbolField.value)) {
    applyPredictionOutcomeState(state, order.ExpressedOutcome);
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    order.OrderQty && toBigWithDefault(order.OrderQty, 0).toFixed()
  );

  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
    resolveRFQCurrencyFromEntity(state.form.symbolField.value, order.Currency)
  );

  const rfqTradingProtocol = getRFQTradingProtocolFromEntity(state, order);
  updateRFQTradingProtocol(state, rfqTradingProtocol);
  handlePricingModeChange(state, order.PricingMode);
  updateSubAccountOrAllocationState(state, order);
  state.form.groupField = state.form.groupField.updateValue(order.Group);

  state.form.parentIDField = state.form.parentIDField.updateValue(order.ParentOrderID);
  state.form.parentType = resolvedParentType;
  state.form.assignToParentField = state.form.assignToParentField.updateValue(order.ParentOrderID != null);
  if (order.ParentOrderID != null && isCareOrWorkingOrderParentType(resolvedParentType)) {
    state.form.fundField = state.form.fundField.setDisabled(true);
    state.form.symbolField = state.form.symbolField.setDisabled(true);
    state.form.sideField = state.form.sideField.setDisabled(true);
  }

  if (order.FixingDetails && order.PricingMode === PricingModeEnum.SpreadToFixing) {
    state.form.fixingField = FixingField.createFromQuote(order).setIsRequired(true);
  }
  if (order.SettleDate) {
    state.form.settleDateField = state.form.settleDateField.updateValue(order.SettleDate);
  }
  if (order.ClearingAccount) {
    state.form.clearingAccountField = state.form.clearingAccountField.updateValue(order.ClearingAccount);
  }

  validate(state);
};

const initializeFieldsFromQuote = (
  state: WritableDraft<RFQState>,
  quote: Quote,
  resolvedParentType?: RFQParentType
) => {
  handleSymbolChange(state, quote.Symbol);

  state.form.sideField = state.form.sideField.updateValue(quote.Side);
  if (SecurityTypesUtils.PredictionOutcome.isProductType(state.form.symbolField.value)) {
    applyPredictionOutcomeState(state, quote.ExpressedOutcome);
  }
  state.form.quantityField = state.form.quantityField.updateValue(
    quote.OrderQty && toBigWithDefault(quote.OrderQty, 0).toFixed()
  );

  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
    resolveRFQCurrencyFromEntity(state.form.symbolField.value, quote.Currency)
  );

  const rfqTradingProtocol = getRFQTradingProtocolFromEntity(state, quote);
  updateRFQTradingProtocol(state, rfqTradingProtocol);
  handlePricingModeChange(state, quote.PricingMode);
  updateSubAccountOrAllocationState(state, quote);
  state.form.sizeCountering = state.form.sizeCountering.updateValue(
    quote.NegotiationParameters?.SizeCountering === ModeEnum.Enabled
  );
  state.form.groupField = state.form.groupField.updateValue(quote.Group);

  state.form.parentIDField = state.form.parentIDField.updateValue(quote.ParentRFQID);
  state.form.parentType = resolvedParentType;
  state.form.assignToParentField = state.form.assignToParentField.updateValue(quote.ParentRFQID != null);
  if (quote.ParentRFQID != null && isCareOrWorkingOrderParentType(resolvedParentType)) {
    state.form.fundField = state.form.fundField.setDisabled(true);
    state.form.symbolField = state.form.symbolField.setDisabled(true);
    state.form.sideField = state.form.sideField.setDisabled(true);
  }

  if (quote.FixingDetails && quote.PricingMode === PricingModeEnum.SpreadToFixing) {
    state.form.fixingField = FixingField.createFromQuote(quote).setIsRequired(true);
  }

  if (quote.ExternalGroup) {
    state.form.externalIDField = state.form.externalIDField.updateValue(quote.ExternalGroup);
  }
  if (quote.ExternalComments) {
    state.form.externalCommentsField = state.form.externalCommentsField.updateValue(quote.ExternalComments);
  }

  if (quote.SettleDate) {
    state.form.settleDateField = state.form.settleDateField.updateValue(quote.SettleDate);
  }
  if (quote.ClearingAccount) {
    state.form.clearingAccountField = state.form.clearingAccountField.updateValue(quote.ClearingAccount);
  }

  validate(state);
};

const validateQuantity = (state: WritableDraft<RFQState>) => {
  state.form.quantityField = state.form.quantityField.validate(
    [numberIsPositive, quantityValidation, careOrderQuantityValidation],
    state
  );
};

const validate = (state: WritableDraft<RFQState>) => {
  validateQuantity(state);
  validateAllocations(state);
};

type CareOrderAssignmentState = Pick<CareOrder, 'Currency' | 'Group' | 'SubAccount' | 'Symbol'> & {
  fund?: string;
};

function applyCareOrderAssignmentState(
  state: WritableDraft<RFQState>,
  careOrder: CareOrderAssignmentState | undefined
) {
  const coSecurity = careOrder?.Symbol
    ? state.referenceData.securities.securitiesBySymbol.get(careOrder.Symbol)
    : undefined;
  state.form.symbolField = state.form.symbolField.updateValue(coSecurity).setDisabled(true);
  if (state.referenceData.settings.enableRFQFormFundField) {
    state.form.fundField = state.form.fundField.updateValue(careOrder?.fund).setDisabled(true);
  }
  state.form.sideField = state.form.sideField.updateValue(undefined); // Care-order RFQs start as 2-way.

  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
    resolveRFQCurrencyFromEntity(coSecurity, careOrder?.Currency)
  );

  if (careOrder?.Group) {
    state.form.groupField = state.form.groupField.updateValue(careOrder.Group).setDisabled(true);
  }
  if (careOrder?.SubAccount) {
    updateSubAccountOrAllocationState(state, { SubAccount: careOrder.SubAccount });
    disableAllocationFields(state, true);
  }
}

function clearCareOrderAssignmentState(
  state: WritableDraft<RFQState>,
  careOrder: CareOrderAssignmentState | undefined
) {
  state.form.symbolField = state.form.symbolField.setDisabled(false);
  state.form.fundField = state.form.fundField.setDisabled(false);
  state.form.sideField = state.form.sideField.setDisabled(false);

  if (careOrder?.Group) {
    state.form.groupField = state.form.groupField.updateValue('').setDisabled(false);
  }
  if (careOrder?.SubAccount) {
    disableAllocationFields(state, false);
  }
}

function getQuantityIncrement(
  security: Security | undefined,
  rfqCurrency: RFQCurrency | undefined,
  currenciesBySymbol: ReadonlyMap<string, Currency>
) {
  if (rfqCurrency == null) {
    return 0;
  }

  if (isRFQContractsCurrency(rfqCurrency)) {
    return 0;
  }

  if (security == null) {
    // Custom option RFQs do not have a security until submit creates one after submission, so use the selected currency min increment when available.
    const currency = getRFQCurrencyAsset(rfqCurrency);
    if (!currency) {
      return 0;
    }
    const minIncrement = currenciesBySymbol.get(currency)?.MinIncrement;
    return minIncrement ? getScaleFromIncrement(minIncrement) : undefined;
  }

  const increment =
    getRFQCurrencyAsset(rfqCurrency) === security.BaseCurrency ? security.MinSizeIncrement : security.MinPriceIncrement;
  return getScaleFromIncrement(increment);
}

/** Gets new state object (resetting the form to an empty form). Keeps necessary pieces of reference data around. */
function getClearFormState(state: WritableDraft<RFQState>): WritableDraft<RFQState> {
  const newState = getInitialState();

  // Keep ref data around. Add more here in the future as necessary.
  newState.referenceData = state.referenceData;
  newState.dependencies = state.dependencies;

  return newState;
}

function applyDefaultAllocationStateOnPrime(state: WritableDraft<RFQState>, params: PrimeRFQParams) {
  if (params.subAccount) {
    // Easy, if a sub account is provided by the primer, always use that
    updateSubAccountOrAllocationState(state, { SubAccount: params.subAccount });
  } else {
    // Else, we need to decide how to default the sub account.
    if (params.defaultToEmptySubAccount) {
      // No sub account was provided by the primer, and they want us to not default. We do this, except for the case when there is only one SA in the system.
      if (state.dependencies.tradableSubAccounts.length === 1) {
        updateSubAccountOrAllocationState(state, {
          SubAccount: state.dependencies.tradableSubAccounts.at(0)?.Name,
        });
      } else {
        updateSubAccountOrAllocationState(state, { SubAccount: EMPTY_SUB_ACCOUNT_SELECTION });
      }
    } else {
      const shouldDefaultSubAccount = !state.form.allocations.length && state.referenceData.defaultSubAccount;
      if (shouldDefaultSubAccount) {
        updateSubAccountOrAllocationState(state, { SubAccount: state.referenceData.defaultSubAccount! });
      }
    }
  }
}

function hydrateDefaultAllocationState(state: WritableDraft<RFQState>) {
  if (!state.form.allocations.length) {
    // allocations fields are an array of fields -- if its just an empty array we have nothing to attach errors to etc when touching the entire form
    // this setup here with hydrating default / empty allocations entries mirrors the order slice setup
    state.form.allocations = mapAllocationsToField([
      { subAccount: state.referenceData.defaultSubAccount || EMPTY_SUB_ACCOUNT_SELECTION, value: '1' },
    ]);
  }
  validateAllocations(state);
}

/** Looks at the settings of our state, and determines what RFQ Trading Protocol to default to */
function getDefaultRFQTradingProtocol(state: WritableDraft<RFQState>): RFQTradingProtocolEnum {
  const { enableNegotiatedRFQFlow, enableTakerMarketplaceETFRFQFlow, rfqFormTradingProtocol } =
    state.referenceData.settings;

  // Persisted protocol should only be honored when the protocol switch is available to the user. The switch is showed when this flag below is true.
  if (enableNegotiatedRFQFlow && rfqFormTradingProtocol != null) {
    return rfqFormTradingProtocol;
  }

  // Else, default based on the ETFRFQFlow flag this first time
  return enableTakerMarketplaceETFRFQFlow ? RFQTradingProtocolEnum.Negotiated : RFQTradingProtocolEnum.Streaming;
}

/** Given either a Quote or Order, will pick out the RFQ Trading Protocol to use. If we can't resolve one (shouldn't happen...), we default. */
function getRFQTradingProtocolFromEntity(
  state: WritableDraft<RFQState>,
  entity: Quote | Order
): RFQTradingProtocolEnum {
  if (entity instanceof Order) {
    if (entity.PricingMode === PricingModeEnum.SpreadToFixing) {
      // Orders do not have an RFQTradingProtocol field. SpreadToFixing implies Negotiated semantics.
      return RFQTradingProtocolEnum.Negotiated;
    }

    return getDefaultRFQTradingProtocol(state);
  }

  // entity is Quote here
  return entity.RFQTradingProtocol ?? getDefaultRFQTradingProtocol(state);
}

/** Call when updating the RFQ Trading Protocol. Also makes updates to related state fields that go along with trading protocol changes. */
function updateRFQTradingProtocol(
  state: WritableDraft<RFQState>,
  rfqTradingProtocol: RFQTradingProtocolEnum | undefined
) {
  state.form.rfqTradingProtocolField = state.form.rfqTradingProtocolField.updateValue(rfqTradingProtocol);
  syncSplitRFQStateWithTradingProtocol(state);
  syncSizeCounteringStateWithTradingProtocol(state);
  syncClearingAccountStateWithTradingProtocol(state);

  const showSpreadToFixingFields =
    state.referenceData.settings.enableTakerMarketplaceETFRFQFlow &&
    rfqTradingProtocol === RFQTradingProtocolEnum.Negotiated &&
    productGroupSupportsRFQSpreadToFixing(state.productGroup);

  if (showSpreadToFixingFields) {
    // If we just flipped to Negotiated protocol, and we support the ETFRFQ flow, then we can set Spread to Fixing.
    // In this case, we need to make the pricing mode field visible, and default the fixing field, both of which become required.
    state.form.pricingModeField = state.form.pricingModeField
      .setIsVisible(true)
      .setDisabled(state.referenceData.settings.disableNonSTFNegotiatedRFQ)
      .updateValue(PricingModeEnum.SpreadToFixing); // default to StF

    const security = state.form.symbolField.value;
    if (security) {
      state.form.fixingField = FixingField.createFromSecurity(
        security,
        state.referenceData.fixingIndices.tradableIndicesBySymbol
      );
    }
    state.form.fixingField = state.form.fixingField.setIsRequired(true);
  } else {
    // Otherwise, pricing mode field and fixing field are no longer relevant to our current setting. Either because we're no longer Protocol = Negotiated,
    // or if we are Protocol=Negotiated but we don't support Spread-to-Fixing.
    state.form.pricingModeField = state.form.pricingModeField.setIsVisible(false).updateValue(undefined);
    state.form.fixingField = new FixingField(); // reset
  }
}

function syncClearingAccountStateWithTradingProtocol(state: WritableDraft<RFQState>): void {
  if (state.form.rfqTradingProtocolField.value !== RFQTradingProtocolEnum.Negotiated) {
    state.form.clearingAccountField = state.form.clearingAccountField.updateValue(undefined);
  }
}

/** Split RFQs, shown to users as Sweeping, owns the Size Countering lock. */
function applySplitRFQsState(state: WritableDraft<RFQState>, splitRFQs: boolean): void {
  state.form.splitRFQs = state.form.splitRFQs.updateValue(splitRFQs);
  state.form.sizeCountering = state.form.sizeCountering.setDisabled(splitRFQs);
  if (splitRFQs) {
    state.form.sizeCountering = state.form.sizeCountering.updateValue(true);
  }
}

function canSelectSplitRFQs(state: WritableDraft<RFQState>): boolean {
  const { enableRFQSweeping, enableRFQSizeCounteringMultitokenCompound, enableRFQSizeCounteringSpot } =
    state.referenceData.settings;

  return isRFQSweepingAvailable({
    security: state.form.symbolField.value,
    rfqTradingProtocol: state.form.rfqTradingProtocolField.value,
    enableRFQSweeping,
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
  });
}

function syncSplitRFQStateWithTradingProtocol(state: WritableDraft<RFQState>): void {
  // Split RFQs - as in sending one separate QuoteRequest per selected Market - is only applicable when protocol is Negotiated.
  // Main reason is technical: we currently CANCEL any Streaming requests that are not active in the RFQ Form. The whole premise of Split
  // RFQs is that you create many RFQs at once, none of which appearing in the RFQ Form. Instead, they "live" in the RFQ Blotter.
  // Secondly, this is only needed for Negotiated currently, so keeping the scope down. But this reason isn't really real in terms of business reason
  if (!canSelectSplitRFQs(state)) {
    applySplitRFQsState(state, false);
  } else if (state.referenceData.settings.rfqFormDefaultToSweeping) {
    // If we are configured to default to sweeping=true, then whenever the sweeping toggle becomes selectable, select it.
    applySplitRFQsState(state, true);
  }
}

function syncSizeCounteringStateWithTradingProtocol(state: WritableDraft<RFQState>): void {
  if (state.form.rfqTradingProtocolField.value !== RFQTradingProtocolEnum.Negotiated) {
    // Size countering is only applicable for Negotiated
    state.form.sizeCountering = state.form.sizeCountering.updateValue(false);
    return;
  }

  // If we are switching from Streaming to Negotiated, and we are defaulted to Size Countering = true, and the security supports it, apply!
  if (
    state.referenceData.settings.rfqFormDefaultToSizeCountering &&
    securityHasSizeCounteringFeatureFlagEnabled(state.form.symbolField.value, state.referenceData.settings)
  ) {
    state.form.sizeCountering = state.form.sizeCountering.updateValue(true);
  }
}

const handleSymbolChange = (state: WritableDraft<RFQState>, symbol: string | undefined) => {
  const security = symbol ? state.referenceData.securities.securitiesBySymbol.get(symbol) : undefined;
  if (!security) {
    state.form.sizeCountering = state.form.sizeCountering.updateValue(false).setDisabled(false);
    return;
  }

  state.form.symbolField = state.form.symbolField.updateValue(security);
  updateProductGroup(state, getGroup(security));
  if (SecurityTypesUtils.PredictionOutcome.isProductType(security)) {
    applyPredictionOutcomeState(state);
  }
  if (!securityHasSizeCounteringFeatureFlagEnabled(security, state.referenceData.settings)) {
    state.form.sizeCountering = state.form.sizeCountering.updateValue(false).setDisabled(false);
  }

  // Set the correct currency on the state
  getRFQFormCurrencySpec(security).onSymbolChanged(state, security);

  // Set up the quantity field based on the security and the currency we derived above
  state.form.quantityField = state.form.quantityField
    .updateValue(security.NormalSize, true)
    .updateScale(
      getQuantityIncrement(
        security,
        state.form.rfqCurrencyField.value,
        state.referenceData.currencies.currenciesBySymbol
      )
    );

  // If we have selected an Option product, we need to create the option model instance
  if (state.productGroup === 'Option') {
    createRFQOptionModel(state, security);
  }

  // On symbol change, keep RFQ allocation state hydrated like the order form does on mount.
  hydrateDefaultAllocationState(state);

  updateRFQTradingProtocol(state, getDefaultRFQTradingProtocol(state));
};

/**
 * When the "Default New Order/RFQ to Counter Currency" setting is enabled, switches the RFQ
 * currency to the quote (counter) currency after the natural currency logic has already run.
 * Qty is cleared only when the currency actually changes (the ONE place qty is cleared here).
 * When the quote currency is already selected, we keep qty as-is so the caller's intended qty is preserved.
 */
function applyCounterCurrencyDefault(state: WritableDraft<RFQState>) {
  if (!state.referenceData.settings.defaultNewOrderToCounterCurrency) {
    return;
  }
  const security = state.form.symbolField.value;
  if (!security) {
    return;
  }
  const quoteCurrency = security.QuoteCurrency;
  if (!quoteCurrency) {
    return;
  }
  const available = getRFQFormCurrencySpec(security)
    .getAvailableCurrencies(security)
    .map(c => c.currency);
  const counterCurrency = createRFQAssetCurrency(quoteCurrency);
  if (!available.some(currency => rfqCurrencyEquals(currency, counterCurrency))) {
    return; // assuming available; naturally excludes compound units
  }
  if (rfqCurrencyEquals(state.form.rfqCurrencyField.value, counterCurrency)) {
    return; // already counter → keep qty (e.g. inverse perp)
  }
  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(counterCurrency);
  state.form.quantityField = state.form.quantityField.updateValue(undefined, true);
}

/**
 * The user can change certain properties of the custom Option model after having already made edits in the rfq form.
 * We only want to reset the rfq form if the late change to the selected option model is "destructive" to the rfq form, as if it changes
 * properties such as market or coin that other properties (such as quantity, currency) have dependencies on
 */
function shouldPreserveRFQFormForCustomOptionUpdate(
  currentOption: Option | WritableDraft<Option> | undefined,
  nextOption: Option
): boolean {
  return (
    // Make sure both are current and next are custom selections, otherwise dont preserve
    currentOption?.isCustom === true &&
    nextOption.isCustom &&
    // Make sure both current and next have a valid (complete) selection, otherwise dont preserve
    currentOption.selection != null &&
    nextOption.selection != null &&
    // coin is dependency for things like quantity, currency etc - reset when changed
    currentOption.data.exchangeField.value?.value != null &&
    currentOption.data.coinField.value?.value != null &&
    // market is an input into market selection. Maybe we can get away with not resetting when market changes, mostly its coin that is important.
    // But we are erring on the side of caution here within reason -- rather be too destructive and not get into a bad state, than not destroy and get into a bad state in some edge case
    currentOption.data.exchangeField.value.value === nextOption.data.exchangeField.value?.value &&
    currentOption.data.coinField.value.value === nextOption.data.coinField.value?.value
  );
}

/** When a custom option is being entered, this will set the necessary currency and quantity state based on the custom option's coin selection */
function applyCustomOptionSymbolAndCurrencyState(state: WritableDraft<RFQState>, option: Option): void {
  state.form.symbolField = getInitialState().form.symbolField;

  if (option.selection?.kind !== 'custom') {
    return;
  }

  const coin = option.data.coinField.value;
  if (!coin) {
    return;
  }

  // Complete custom options start in base currency unless the RFQ already has a currency.
  const rfqCurrency = state.form.rfqCurrencyField.value ?? createRFQAssetCurrency(coin.baseCurrency);
  state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(rfqCurrency, true);
  state.form.quantityField = state.form.quantityField.updateScale(
    getQuantityIncrement(undefined, rfqCurrency, state.referenceData.currencies.currenciesBySymbol)
  );
}

const rfqSlice = createSlice({
  name: 'RFQ',
  initialState: getInitialState(),
  reducers: {
    ...otcRepoFormReducers,
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;
    },
    setDependencies: (state, action: PayloadAction<RFQDependencies>) => {
      state.dependencies = action.payload;

      if (!state.currentQuote) {
        if (state.form.allocations.length && action.payload.tradableSubAccounts?.length != null) {
          // Filter out sub-accounts that are no longer tradeable
          state.form.allocations = state.form.allocations.filter(a =>
            a.subAccountField.value
              ? action.payload.tradableSubAccounts!.find(sb => sb.Name === a.subAccountField.value)
              : true
          );
        }

        validateAllocations(state);
      }
    },
    hydrateDefaultAllocations: state => {
      hydrateDefaultAllocationState(state);
    },

    resubmitRFQ: (state, action: PayloadAction<{ quoteOrOrder: Quote | Order; parentType?: RFQParentType }>) => {
      state = getClearFormState(state);
      if (isOrder(action.payload.quoteOrOrder)) {
        initializeFieldsFromOrder(state, action.payload.quoteOrOrder, action.payload.parentType);
      } else {
        initializeFieldsFromQuote(state, action.payload.quoteOrOrder, action.payload.parentType);
      }

      return state;
    },
    showRFQ: (state, action: PayloadAction<{ quote: Quote; parentType?: RFQParentType }>) => {
      state = getClearFormState(state);
      initializeFieldsFromQuote(state, action.payload.quote, action.payload.parentType);

      state.currentQuote = action.payload.quote;
      return state;
    },
    primeNewRFQForm: (state, action: PayloadAction<PrimeRFQParams>) => {
      state = getClearFormState(state);
      const parentID = action.payload.parent?.id;
      const hasCareOrWorkingParent = isCareOrWorkingOrderParentType(action.payload.parent?.type);

      const symbol = action.payload.symbol;
      handleSymbolChange(state, symbol);

      state.form.sideField = state.form.sideField.updateValue(action.payload.side);
      if (SecurityTypesUtils.PredictionOutcome.isProductType(state.form.symbolField.value)) {
        state.form.sideField = state.form.sideField.updateValue(undefined);
      }
      state.form.rfqMarketsField = getInitialState().form.rfqMarketsField;

      // Differentiate between priming with empty initial quantity vs using security's NormalSize
      if (action.payload.orderQty != null) {
        state.form.quantityField = state.form.quantityField
          // removes trailing zeroes after the decimal place if we toFixed() a Big object instead of just setting the rawString
          .updateValue(action.payload.orderQty && toBigWithDefault(action.payload.orderQty, 0).toFixed());
      }

      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
        resolveRFQCurrencyFromEntity(state.form.symbolField.value, action.payload.currency)
      );

      state.form.groupField = state.form.groupField.updateValue(action.payload.group);

      applyDefaultAllocationStateOnPrime(state, action.payload);

      if (action.payload.externalID) {
        state.form.externalIDField = state.form.externalIDField.updateValue(action.payload.externalID);
      }
      if (parentID != null || action.payload.externalComments) {
        state.form.externalCommentsField = state.form.externalCommentsField.updateValue(
          action.payload.externalComments
        );
      }
      if (action.payload.settleDate) {
        state.form.settleDateField = state.form.settleDateField.updateValue(action.payload.settleDate);
      }
      if (action.payload.clearingAccount) {
        state.form.clearingAccountField = state.form.clearingAccountField.updateValue(action.payload.clearingAccount);
      }
      if (action.payload.rfqTradingProtocol != null) {
        // If a protocol is provided in the primer we always use it, regardless of stored user defaults.
        updateRFQTradingProtocol(state, action.payload.rfqTradingProtocol);
      }

      state.form.parentIDField = state.form.parentIDField.updateValue(parentID);
      state.form.parentType = action.payload.parent?.type;
      state.form.assignToParentField = state.form.assignToParentField.updateValue(parentID != null);

      if (parentID != null && hasCareOrWorkingParent) {
        // If we are primed from a parent, there are certain fields you are not allowed to change
        state.form.symbolField = state.form.symbolField.setDisabled(true);
        state.form.fundField = state.form.fundField.setDisabled(true);

        if (action.payload.group) {
          state.form.groupField = state.form.groupField.setDisabled(true);
        }

        // If we were primed with a sub account from a parent entity we are locked into this sub account
        if (action.payload.subAccount) {
          disableAllocationFields(state, true);
        }
      }

      // Skip when primed from a parent (care/working order): the RFQ is locked to the
      // parent's currency and remaining size, so we must not rewrite them.
      if (!action.payload.ignoreCounterCurrencyDefault && !hasCareOrWorkingParent) {
        applyCounterCurrencyDefault(state);
      }

      validate(state);
      return state; // we create a new cleared state instance above, so we need to return it
    },
    setSide: (state, action: PayloadAction<SideEnum | undefined>) => {
      state.form.sideField = state.form.sideField.updateValue(action.payload);
      validate(state);
    },
    setOutcome: (state, action: PayloadAction<ExpressedOutcomeEnum>) => {
      state.form.outcomeField = state.form.outcomeField.updateValue(action.payload);
      state.form.sideField = state.form.sideField.updateValue(undefined);
      validate(state);
    },
    setFund: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload == null) {
        // fund was cleared. We don't need to clear any form state in this case, we can keep what is selected
        state.form.fundField = state.form.fundField.updateValue(undefined);
        return;
      }

      const fundSymbols = state.referenceData.funds.fundsBySymbol.get(action.payload)?.TradableSecurities;
      const currentSymbol = state.form.symbolField.value?.Symbol;
      const newFundChangesSymbol = !currentSymbol || !fundSymbols?.includes(currentSymbol);
      if (newFundChangesSymbol) {
        state = getClearFormState(state);
        state.form.fundField = state.form.fundField.updateValue(action.payload);
        // Default to the compound fund symbol in the list of tradable securities of the fund
        const compoundFundSymbol = fundSymbols?.find(symbol =>
          SecurityTypesUtils.Compound.isProductType(state.referenceData.securities.securitiesBySymbol.get(symbol))
        );
        const defaultFundSymbol = compoundFundSymbol ?? fundSymbols?.at(0);
        handleSymbolChange(state, defaultFundSymbol);
      } else {
        state.form.fundField = state.form.fundField.updateValue(action.payload);
      }

      // In the newFundChangesSymbol code path we are changing the state reference, so need to return the new one
      return state;
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      const productGroup = state.productGroup;
      state = getClearFormState(state);
      if (action.payload == null) {
        updateProductGroup(state, productGroup);
      }
      handleSymbolChange(state, action.payload);
      applyCounterCurrencyDefault(state);
      return state;
    },
    setCustomSymbolOnCreation: (state, action: PayloadAction<Security>) => {
      // This action is used for option symbols created on CustomInstrumentQuoteRequest. This will be called while a QuoteRequest is already
      // in flight. This action "hot-swaps" the current "custom" Option model out for a properly "listed" Security Option model, and also sets the rfqSlice's symbolField.value.
      const security = action.payload;
      state.form.symbolField = state.form.symbolField.updateValue(security, true);
      updateProductGroup(state, getGroup(security));
      if (SecurityTypesUtils.Option.isType(security)) {
        createRFQOptionModel(state, security);
      }
    },
    setProductGroup: (state, action: PayloadAction<ProductGroup>) => {
      const newState = getClearFormState(state); // wipe everything
      updateProductGroup(newState, action.payload);
      if (action.payload === 'Option') {
        createRFQOptionModel(newState);
      }
      if (action.payload === 'OTC') {
        initOTCRepoStateOnProductGroupSet(newState);
      }
      return newState;
    },
    updateOption: (state, action: PayloadAction<Option>) => {
      if (shouldPreserveRFQFormForCustomOptionUpdate(state.form.option, action.payload)) {
        // Same custom market/coin; keep the RFQ form.
        state.form.option = action.payload;
        return;
      }

      // Market/coin changes, mode switches, and listed option changes reroute the RFQ.
      const newState = getClearFormState(state);
      updateProductGroup(newState, state.productGroup);
      newState.form.option = action.payload;

      if (action.payload.isCustom) {
        // Custom options have no listed symbol until submit/creation.
        applyCustomOptionSymbolAndCurrencyState(newState, action.payload);
        return newState;
      }

      const symbol = action.payload.security?.Symbol;
      if (symbol) {
        handleSymbolChange(newState, symbol);
      } else {
        newState.form.symbolField = newState.form.symbolField.updateValue(undefined);
      }

      return newState;
    },
    setQuantity: (state, action: PayloadAction<string>) => {
      state.form.quantityField = state.form.quantityField.updateValue(action.payload);
      validateQuantity(state);
    },
    toggleAssignToParent: state => {
      const nextAssignToParentValue = !state.form.assignToParentField.value;
      state.form.assignToParentField = state.form.assignToParentField.updateValue(nextAssignToParentValue);

      const currentCareOrder = state.currentCareOrder;
      if (nextAssignToParentValue === true) {
        applyCareOrderAssignmentState(state, currentCareOrder);
      } else {
        clearCareOrderAssignmentState(state, currentCareOrder);
      }

      validate(state);
    },
    setRFQCurrency: (state, action: PayloadAction<RFQCurrency>) => {
      const quantityIncrement = getQuantityIncrement(
        state.form.symbolField.value,
        action.payload,
        state.referenceData.currencies.currenciesBySymbol
      );
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(action.payload);
      state.form.quantityField = state.form.quantityField.updateValue('', true).updateScale(quantityIncrement);
      validateQuantity(state);
    },
    setCurrentQuote: (state, { payload: quote }: PayloadAction<Quote>) => {
      state.quoteRequested = false;

      // If we've reached a terminal state, go back to showing the form as it was
      if (quote.isTerminal) {
        const hasEphemeralSecurity = isEphemeralRFQSecurity(state.form.symbolField.value);
        // Some RFQ symbols are generated just for a single quote lifecycle. Once that quote
        // leaves currentQuote, the form must drop the generated symbol instead of keeping a stale selection around.
        if (hasEphemeralSecurity) {
          state = getClearFormState(state);
        }

        state.isLoading = false;
        state.currentQuote = undefined;
        return state;
      }

      state.isLoading = quote.isPending;
      state.currentQuote = quote;
    },
    setCurrentCareOrder: (state, { payload: co }: PayloadAction<CareOrder | undefined>) => {
      // Called when the Care Order that is our parent order comes in
      const wasAssignedToParent = state.form.assignToParentField.value === true;
      state.currentCareOrder = co;

      if (state.currentCareOrder?.isComplete) {
        if (wasAssignedToParent) {
          clearCareOrderAssignmentState(state, co);
        }
        state.form.assignToParentField = state.form.assignToParentField.updateValue(false);
      }

      if (state.form.assignToParentField.value === true) {
        applyCareOrderAssignmentState(state, co);
      }
      validate(state);
    },
    setAllocations: (state, action: PayloadAction<Allocation[]>) => {
      state.form.allocations = mapAllocationsToField(action.payload, state.form.allocationValueTypeField.value, true);
      validateAllocations(state);

      const resetOrderAccountsOnSubAccountChange =
        state.referenceData.settings.enableAccountSegregation && // only do this if trading acct segregation is enabled
        state.referenceData.settings.resetOrderAccountsOnSubAccountChange;

      // we only do our resetting logic if we are just working with 1 sub account, and not several. Doing < 2 also catches the case where
      // we go from 0->1 and 1->0, since those are valid "reset" state transitions as well.
      const isSingleSubAccountChange = state.form.allocations.length < 2 && action.payload.length < 2;
      if (resetOrderAccountsOnSubAccountChange && isSingleSubAccountChange) {
        state.form.rfqMarketsField = getInitialState().form.rfqMarketsField; // reset
      }
    },
    setAllocationValueType: (state, action: PayloadAction<AllocationValueTypeEnum>) => {
      const mappedToNewUnit = state.form.allocations.map(pair => ({
        subAccountField: pair.subAccountField,
        valueField: pair.valueField
          .updateUnit(action.payload === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal)
          .updateValue(
            state.form.allocationValueTypeField.value === AllocationValueTypeEnum.Percentage
              ? pair.valueField.displayValue
              : pair.valueField.value
          ),
      }));
      state.form.allocations = mappedToNewUnit;
      state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(action.payload);
      validateAllocations(state);
    },
    setRFQMarkets: (state, action: PayloadAction<{ markets: string[]; aggregation?: string }>) => {
      const newValue: MarketSelectorValue = action.payload.aggregation
        ? { type: 'preset', presetID: action.payload.aggregation, selections: action.payload.markets }
        : { type: 'custom', selections: action.payload.markets };

      state.form.rfqMarketsField = state.form.rfqMarketsField.updateValue(newValue);
    },
    setPricingMode: (state, { payload: pricingMode }: PayloadAction<PricingModeEnum | undefined>) => {
      handlePricingModeChange(state, pricingMode);
    },
    setRFQTradingProtocol: (
      state,
      { payload: rfqTradingProtocol }: PayloadAction<RFQTradingProtocolEnum | undefined>
    ) => {
      state.form.rfqMarketsField = getInitialState().form.rfqMarketsField;
      updateRFQTradingProtocol(state, rfqTradingProtocol);
    },
    initializeRFQMarketsField: (
      state,
      action: PayloadAction<{
        desiredValue: MarketSelectorValue | undefined;
        items: MarketSelectorItem[];
        /** Provide directly to override the default seting held in the redux store */
        defaultToEmptyMarkets?: boolean;
      }>
    ) => {
      state.form.rfqMarketsField = state.form.rfqMarketsField.initialize({
        desiredValue: action.payload.desiredValue,
        items: action.payload.items,
        settings: {
          defaultToEmpty:
            action.payload.defaultToEmptyMarkets ?? state.referenceData.settings.defaultToEmptyOrderMarkets,
          defaultMarketAccounts: state.referenceData.settings.defaultMarketAccounts,
          defaultAggregationId: state.referenceData.settings.defaultAggregationId,
          aggregationPresets: state.referenceData.tradingAggregationPresets,
        },
      });
    },

    handleRFQMarketSelectorItemsChange: (state, action: PayloadAction<MarketSelectorItem[]>) => {
      state.form.rfqMarketsField = state.form.rfqMarketsField.updateItems(action.payload);
    },
    setGroup: (state, action: PayloadAction<string | undefined>) => {
      state.form.groupField = state.form.groupField.updateValue(action.payload);
    },
    setFixingIndex: (state, action: PayloadAction<{ symbol: string; index: string | undefined }>) => {
      state.form.fixingField = state.form.fixingField.setFixing(action.payload.symbol, action.payload.index);
    },
    setExternalID: (state, action: PayloadAction<string | undefined>) => {
      state.form.externalIDField = state.form.externalIDField.updateValue(action.payload);
    },
    setExternalComments: (state, action: PayloadAction<string | undefined>) => {
      state.form.externalCommentsField = state.form.externalCommentsField.updateValue(action.payload);
    },
    setSettleDate: (state, action: PayloadAction<string | undefined>) => {
      state.form.settleDateField = state.form.settleDateField.updateValue(action.payload);
    },
    setClearingAccount: (state, action: PayloadAction<string | undefined>) => {
      state.form.clearingAccountField = state.form.clearingAccountField.updateValue(action.payload);
    },
    setQuoteRequested: state => {
      state.isLoading = true;
      state.quoteRequested = true;
    },
    setSplitRFQs: (state, action: PayloadAction<boolean>) => {
      applySplitRFQsState(state, action.payload);
    },
    setSizeCounteringEnabled: (state, action: PayloadAction<boolean>) => {
      state.form.sizeCountering = state.form.sizeCountering.updateValue(action.payload);
    },
    // Pretty much just the inverse of what we do directly above in the setQuoteRequested action
    onQuoteRequestFailed: state => {
      state.isLoading = false;
      state.quoteRequested = false;
    },
    onNewOrderSingleSent: state => {
      // NOS is sent when accepting a quote
      state.isLoading = true;
    },
    onNewOrderSingleFailed: state => {
      state.isLoading = false;
    },
    /**
     * The various entry point thunks to the RFQ Slice (priming, resubmitting, onMount) all run this action after completion to let others know that initialization is done
     * Currently the only thing we actually initialize is the markets field
     */
    setInitialized: state => {
      state.initialized = true;
    },
    onUnMount: state => {
      // This action is called whenever the RFQ form is closed. The user has not clicked Clear, they have just left the form for some other reason.
      // When adding new fields to this form, you should always add it into this action in one way or another.
      const newState: WritableDraft<RFQState> = getClearFormState(state);

      // We choose to not persist any state if our state is tied to a parent entity (eg a Care Order), or
      // if we are in OTC due to unhandled complexity there. This is just for simplicity.
      const shouldPersist = state.form.parentIDField.value == null && state.productGroup !== 'OTC';

      if (shouldPersist) {
        // Persist all these fields for when they open the form next
        newState.form.fundField = state.form.fundField.setTouched(false);
        newState.form.symbolField = state.form.symbolField.setTouched(false);
        updateProductGroup(newState, state.productGroup);
        newState.form.fixingField = state.form.fixingField.setTouched(false);
        newState.form.sideField = state.form.sideField.setTouched(false);
        newState.form.outcomeField = state.form.outcomeField.setTouched(false);
        newState.form.quantityField = state.form.quantityField.setTouched(false);
        newState.form.rfqCurrencyField = state.form.rfqCurrencyField.setTouched(false);
        newState.form.rfqMarketsField = state.form.rfqMarketsField.setTouched(false);
        newState.form.groupField = state.form.groupField.setTouched(false);
        newState.form.allocationValueTypeField = state.form.allocationValueTypeField.setTouched(false);
        newState.form.allocations = state.form.allocations.map(alloc => ({
          subAccountField: alloc.subAccountField.setTouched(false),
          valueField: alloc.valueField.setTouched(false),
        }));
        newState.form.rfqTradingProtocolField = state.form.rfqTradingProtocolField.setTouched(false);
        newState.form.pricingModeField = state.form.pricingModeField.setTouched(false);
        newState.form.externalIDField = state.form.externalIDField.setTouched(false);
        newState.form.externalCommentsField = state.form.externalCommentsField.setTouched(false);
        newState.form.settleDateField = state.form.settleDateField.setTouched(false);
        newState.form.clearingAccountField = state.form.clearingAccountField.setTouched(false);
        newState.form.splitRFQs = state.form.splitRFQs.setTouched(false);
        newState.form.sizeCountering = state.form.sizeCountering.setTouched(false);
        newState.form.option = state.form.option;
      }

      return newState;
    },
    // different to onUnMount, this is called when the user specifically clicks the "Clear" button. It resets state.
    clear: state => {
      return getClearFormState(state);
    },
    touchAll: state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = state.form[key].setTouched(true);
        }
      });
      if (state.subProductType === SubProductTypeEnum.OTCRepo) {
        touchOTCRepoForm(state);
      }

      touchAllocations(state, true);
    },
  },
});

/**
 * The referenceData populated in all our slices is app-wide, so it includes all options.
 * For RFQ, we only support options tradable at specific venues (currently FalconX and STS Digital).
 * See isRFQSupportedOption in utils.ts when adding a new venue.
 */
export function getRFQOptionModelRefData(input: OMSReferenceDataState): OptionModelReferenceData {
  const rfqOptions = input.options.options.filter(isRFQSupportedOption);
  const marketsAllowingCustomOptions = input.settings.enableSTSDigitalCustomOptionRFQ
    ? [STS_DIGITAL_MARKET_NAME]
    : undefined;

  // Freeze this object so immer only has to check if this wrapping object itself was written into, instead of having to deep-check
  return freeze(
    {
      options: {
        options: rfqOptions,
        optionSecurityBySymbol: new Map(rfqOptions.map(option => [option.Symbol, option])),
        strikesByOptionKey: getStrikesByOptionKey(rfqOptions),
        expirationByMarketByCurrencyIdentity: getExpirationByMarketByCurrencyIdentity(rfqOptions),
      },
      markets: {
        marketsByName: input.markets.marketsByName,
      },
      marketAccounts: {
        marketAccountsByMarket: input.marketAccounts.marketAccountsByMarket,
      },
      marketsAllowingCustomOptions,
    },
    true
  );
}

/** Wraps the Option model creation in some RFQ-specific logic to ensure creation is done with the correct inputs */
function createRFQOptionModel(state: WritableDraft<RFQState>, security?: Security) {
  // Do original() so we don't iterate over huge arrays of drafts. This leads to a bunch of immer work on the back-end of the operation.
  const referenceData = isDraft(state.referenceData)
    ? (original(state.referenceData) ?? state.referenceData)
    : state.referenceData;
  const optionModelRefData = getRFQOptionModelRefData(referenceData as OMSReferenceDataState);

  state.form.option =
    security && SecurityTypesUtils.Option.isType(security)
      ? Option.createFromSecurity(optionModelRefData, security)
      : Option.createFromBlank(optionModelRefData);
}

const primeNewRFQFormThunk = createAsyncThunk<void, PrimeRFQParams, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/primeNewRFQFormThunk',
  (params, thunk) => {
    thunk.dispatch(rfqSlice.actions.primeNewRFQForm(params));

    // If the rfq markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      let desiredMarketSelectorValue: MarketSelectorValue | undefined = undefined;
      if (params.marketAccounts) {
        desiredMarketSelectorValue = params.aggregation
          ? { type: 'preset', presetID: params.aggregation, selections: params.marketAccounts }
          : { type: 'custom', selections: params.marketAccounts };
      }

      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: desiredMarketSelectorValue }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

/**
 * Mounting is a special thunk. It is one of a handful of entry points into the RFQ Form. It is the only entry point thunk which can be
 * called _after_ someone else has already initialized. This is what differentiates it from prime, modify, resubmit. Because of this,
 * onMountThunk needs to be aware of the rfq.initialized state.
 *
 * onMountThunk is responsible for making sure that we resume operation correctly if the user closes and just re-opens the form
 * where state is persisted. For example, we need to re-kick off the Markets Field with its async listeners.
 */
const onMountThunk = createAsyncThunk<void, void, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/onMountThunk',
  (_, thunk) => {
    thunk.dispatch(rfqSlice.actions.hydrateDefaultAllocations());

    // If we already have a symbol set, but nobody has kicked off initialization yet, then we need to perform an init steps.
    // This happens in the RFQ Form and not the Order Form because the RFQ Form remembers your selected Symbol despite closing.
    if (thunk.getState().rfq.form.symbolField.value != null && !thunk.getState().rfq.initialized) {
      // The Markets field is a persisted property. If something was set, we try to re-init to it (things might've changed tho)
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: thunk.getState().rfq.form.rfqMarketsField.value }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

const setSymbolThunk = createAsyncThunk<void, string | undefined, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setSymbolThunk',
  (symbol, thunk) => {
    thunk.dispatch(rfqSlice.actions.setSymbol(symbol));

    // If the rfq markets field was reset after the dispatch directly above, we start the initialization of market selector items
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

// Resubmitting a child order/quote has the same parent ID ambiguity as opening it: resolve the parent type first,
// otherwise the parent-order listener can follow CareOrder for a WorkingOrder-backed RFQ.
const resubmitRFQThunk = createAsyncThunk<void, Quote | Order, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/resubmitRFQThunk',
  async (quoteOrOrder, thunk) => {
    const parentID = quoteOrOrder instanceof Order ? quoteOrOrder.ParentOrderID : quoteOrOrder.ParentRFQID;
    let parentType: RFQParentType | undefined;

    if (parentID != null) {
      // Try to figure out the parent type of the parent id that is populated.
      // Newer Quotes will all have a ParentOrderType, and CO+WO will be backfilled, but we still need the fallback to the async resolution for now for backwards compatibility before
      // the backfill is performed on all the environments that do CareOrder trading.
      const knownParentType = getParentTypeFromParentOrder(quoteOrOrder);
      parentType = knownParentType ?? (await resolveParentTypeFromParentOrder(parentID, thunk.dispatch));
    }

    thunk.dispatch(rfqSlice.actions.resubmitRFQ({ quoteOrOrder, parentType }));

    const tradedMarketAccounts =
      quoteOrOrder instanceof Order
        ? compact(
            quoteOrOrder.Markets.filter(m => m.MarketStatus !== OrderMarketStatusEnum.Disabled).map(
              m => m.MarketAccount
            )
          )
        : // Quote.Markets don't have a MarketStatus it seems
          compact(quoteOrOrder.Markets.map(m => m.MarketAccount));

    thunk.dispatch(
      initializeRFQMarketsFieldThunk({ desiredValue: { type: 'custom', selections: tradedMarketAccounts } })
    );

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

// Plain quotes only tell us ParentRFQID, not whether it points to a CareOrder or WorkingOrder.
// Resolve that first so the reducer state drives the parent-order listener to the right stream.
const showRFQThunk = createAsyncThunk<void, Quote, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/showRFQThunk',
  async (quote, thunk) => {
    const parentID = quote.ParentRFQID;

    if (parentID == null) {
      thunk.dispatch(rfqSlice.actions.showRFQ({ quote }));
      return;
    }

    // Try to figure out the parent type of the parent id that is populated.
    // Newer Quotes will all have a ParentOrderType, and CO+WO will be backfilled, but we still need the fallback to the async resolution for now for backwards compatibility before
    // the backfill is performed on all the environments that do CareOrder trading.
    const knownParentType = getParentTypeFromParentOrder(quote);
    const parentType = knownParentType ?? (await resolveParentTypeFromParentOrder(parentID, thunk.dispatch));
    thunk.dispatch(rfqSlice.actions.showRFQ({ quote, parentType }));
  }
);

const setAllocationsThunk = createAsyncThunk<void, Allocation[], { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setAllocationsThunk',
  (params, thunk) => {
    thunk.dispatch(rfqSlice.actions.setAllocations(params));

    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      // If the setAllocations action cleared the rfqMarketsField, then we kick off a initialization thunk here
      // Override default to empty to be false for this action
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined, defaultToEmptyMarkets: false }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

const setRFQTradingProtocolThunk = createAsyncThunk<
  void,
  RFQTradingProtocolEnum | undefined,
  { state: AppState; extra: AppExtraArgument }
>('rfqSlice/setRFQTradingProtocolThunk', (params, thunk) => {
  thunk.dispatch(rfqSlice.actions.setRFQTradingProtocol(params));

  if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
    // If the setRFQTradingProtocol action cleared the rfqMarketsField, then we kick off a initialization thunk here
    // Override default to empty to be false for this action
    thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined, defaultToEmptyMarkets: false }));
  }

  thunk.dispatch(rfqSlice.actions.setInitialized());
});

/**
 * UpdateOption is kind of like a proxy for setSymbol. Due to this, we need to treat it like an entry point to the RFQ Slice,
 * and make sure that we instantiate the slice if necessary ("instantiation" = init the markets field)
 */
const updateOptionThunk = createAsyncThunk<void, Option, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/updateOptionThunk',
  (option, thunk) => {
    thunk.dispatch(rfqSlice.actions.updateOption(option));

    const state = thunk.getState();
    const canInitializeRFQMarkets =
      !state.rfq.form.rfqMarketsField.initialized && rfqSelectors.selectHasRFQSymbolSelection(state);
    if (canInitializeRFQMarkets) {
      // Half-built custom options have no symbol route yet. Wait until the option is listed or the
      // custom selection is complete, otherwise we initialize markets against "nothing" and block
      // the later complete custom selection from deriving its market accounts.
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);

const setFundThunk = createAsyncThunk<void, string | undefined, { state: AppState; extra: AppExtraArgument }>(
  'rfqSlice/setFundThunk',
  (fund, thunk) => {
    thunk.dispatch(rfqSlice.actions.setFund(fund));

    // Setting the fund might trigger a change in symbol and a form reset, which might lead to needing to re-init here
    if (!thunk.getState().rfq.form.rfqMarketsField.initialized) {
      thunk.dispatch(initializeRFQMarketsFieldThunk({ desiredValue: undefined }));
    }

    thunk.dispatch(rfqSlice.actions.setInitialized());
  }
);
export const rfqSliceActions = {
  primeNewRFQForm: primeNewRFQFormThunk,
  onMount: onMountThunk,
  setSymbol: setSymbolThunk,
  setCustomSymbolOnCreation: rfqSlice.actions.setCustomSymbolOnCreation,
  resubmitRFQ: resubmitRFQThunk,
  showRFQ: showRFQThunk,
  setAllocations: setAllocationsThunk,
  setRFQTradingProtocol: setRFQTradingProtocolThunk,
  updateOption: updateOptionThunk,
  setFund: setFundThunk,
  setReferenceData: rfqSlice.actions.setReferenceData,
  setDependencies: rfqSlice.actions.setDependencies,
  setSide: rfqSlice.actions.setSide,
  setOutcome: rfqSlice.actions.setOutcome,
  setQuantity: rfqSlice.actions.setQuantity,
  setAllocationValueType: rfqSlice.actions.setAllocationValueType,
  setPricingMode: rfqSlice.actions.setPricingMode,
  setGroup: rfqSlice.actions.setGroup,
  setFixingIndex: rfqSlice.actions.setFixingIndex,
  setExternalID: rfqSlice.actions.setExternalID,
  setExternalComments: rfqSlice.actions.setExternalComments,
  setSettleDate: rfqSlice.actions.setSettleDate,
  setClearingAccount: rfqSlice.actions.setClearingAccount,
  setRFQCurrency: rfqSlice.actions.setRFQCurrency,
  setCurrentQuote: rfqSlice.actions.setCurrentQuote,
  setCurrentCareOrder: rfqSlice.actions.setCurrentCareOrder,
  toggleAssignToParent: rfqSlice.actions.toggleAssignToParent,
  setQuoteRequested: rfqSlice.actions.setQuoteRequested,
  setSplitRFQs: rfqSlice.actions.setSplitRFQs,
  setSizeCounteringEnabled: rfqSlice.actions.setSizeCounteringEnabled,
  onNewOrderSingleSent: rfqSlice.actions.onNewOrderSingleSent,
  onNewOrderSingleFailed: rfqSlice.actions.onNewOrderSingleFailed,
  onQuoteRequestFailed: rfqSlice.actions.onQuoteRequestFailed,
  onUnMount: rfqSlice.actions.onUnMount,
  clear: rfqSlice.actions.clear,
  setRFQMarkets: rfqSlice.actions.setRFQMarkets,
  setProductGroup: rfqSlice.actions.setProductGroup,
  handleRFQMarketSelectorItemsChange: rfqSlice.actions.handleRFQMarketSelectorItemsChange,
  touchAll: rfqSlice.actions.touchAll,
};

export function setupListeners(startListening: AppStateListenerStart) {
  const unsubscribeCareOrderForModify = startCareOrderParentListener({
    startListening,
    matcher: action =>
      rfqSlice.actions.primeNewRFQForm.match(action) ||
      rfqSlice.actions.resubmitRFQ.match(action) ||
      rfqSlice.actions.showRFQ.match(action),
    openedView: OMSView.RFQForm,
    selectParentID: state => state.rfq.form.parentIDField.value,
    selectParentType: state => state.rfq.form.parentType,
    setCurrentCareOrder: rfqSlice.actions.setCurrentCareOrder,
  });

  const unsubscribeMarketSelectorItemsListener = startListening(
    getMarketSelectorItemsListener({
      id: 'rfqslice',
      predicate: action => initializeRFQMarketsFieldThunk.fulfilled.match(action),
      selectMSSRequestParams: rfqSelectors.selectMSSRequestParams,
      startListening,
      selectItemBuildingParams: rfqSelectors.selectBuildRFQMarketSelectorItemsParams,
      buildItems: buildMarketSelectorItems,
      onItemChange: (items, dispatch) => dispatch(rfqSlice.actions.handleRFQMarketSelectorItemsChange(items)),
      OMSView: OMSView.RFQForm,
    })
  );

  const unsubscribePositionSubListener = startListening({
    predicate: (_, next, prev) => {
      // We run the effect if the OMS was just opened to RFQForm.
      return prev.OMS.openedView !== next.OMS.openedView && next.OMS.openedView === OMSView.RFQForm;
    },
    effect: async (_, listener) => {
      /**
       * This effect will kick off the Positions subscription which is later used in various RFQSlice selectors upon opening the RFQForm.
       * The listener will close the subscription when the user leaves the RFQForm.
       */
      listener.cancelActiveListeners();
      const positionSub = listener.dispatch(
        streamingDataSlice.endpoints.getPositions.initiate(RFQSLICE_POSITION_REQUEST_PARAMS)
      );

      listener.signal.addEventListener('abort', () => {
        positionSub.unsubscribe();
      });

      // Keep the listener alive until the order form is closed.
      await listener.condition(() => {
        return listener.getState().OMS.openedView !== OMSView.RFQForm;
      });
    },
  });

  return () => {
    unsubscribeCareOrderForModify();
    unsubscribeMarketSelectorItemsListener();
    unsubscribePositionSubListener();
  };
}
export const rfqSliceReducer = rfqSlice.reducer;
